import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "../../context/AuthContext";
import { useSearchParams } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { Badge } from "../../components/ui/badge";
import { 
  Plus, 
  Pencil, 
  Trash2, 
  Calendar,
  Loader2,
  Dumbbell,
  X,
  ChevronUp,
  ChevronDown,
  Link2,
  Save,
  Zap,
  Timer,
  TrendingDown,
  Target,
  Pause,
  ArrowDownUp,
  Minus,
  RotateCcw
} from "lucide-react";
import { toast } from "sonner";
import { cn } from "../../lib/utils";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DAY_CODES = ["A", "B", "C", "D", "E", "F"];

// Técnicas de intensidad disponibles
const INTENSITY_TECHNIQUES = [
  { id: "myo_reps", name: "Myo-reps", icon: Zap, color: "#F59E0B", hasConfig: true, configLabel: "Estructura", configPlaceholder: "12 + 3x3" },
  { id: "dropset", name: "Drop-set", icon: TrendingDown, color: "#EF4444", hasConfig: true, configLabel: "Drops", configPlaceholder: "2", configType: "number" },
  { id: "isometric", name: "Isométrico", icon: Timer, color: "#3B82F6", hasConfig: true, configLabel: "Segundos", configPlaceholder: "10", configType: "number" },
  { id: "topset", name: "Top set", icon: Target, color: "#8B5CF6", hasConfig: true, configLabel: "Intensidad", configPlaceholder: "RPE 9" },
  { id: "deadstop", name: "Dead-stop", icon: Pause, color: "#EC4899", hasConfig: false },
  { id: "partials", name: "Parciales", icon: ArrowDownUp, color: "#14B8A6", hasConfig: false },
  { id: "negatives", name: "Negativas", icon: Minus, color: "#F97316", hasConfig: false },
  { id: "zigzag", name: "Zig-zag", icon: RotateCcw, color: "#06B6D4", hasConfig: false },
  { id: "rm", name: "RM", icon: Target, color: "#D4AF37", hasConfig: true, configLabel: "RM", configPlaceholder: "5", configType: "number" },
];

const AdminWorkoutDays = () => {
  const { getAuthHeaders } = useAuth();
  const [searchParams] = useSearchParams();
  const [workoutDays, setWorkoutDays] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [exercises, setExercises] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProgram, setSelectedProgram] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingDay, setEditingDay] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);

  const [formData, setFormData] = useState({
    program_id: "",
    code: "",
    exercises: [],
  });

  useEffect(() => {
    fetchData();
  }, []);

  // Handle URL param for program selection
  useEffect(() => {
    const programId = searchParams.get("program");
    if (programId && programs.length > 0) {
      setSelectedProgram(programId);
    }
  }, [searchParams, programs]);

  useEffect(() => {
    if (selectedProgram) {
      fetchWorkoutDays(selectedProgram);
    }
  }, [selectedProgram]);

  const fetchData = async () => {
    try {
      const [programsRes, exercisesRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/programs`, { headers: getAuthHeaders() }),
        axios.get(`${API_URL}/api/admin/exercises`, { headers: getAuthHeaders() }),
      ]);
      setPrograms(programsRes.data);
      setExercises(exercisesRes.data);
      
      // Check URL param first, then default to first program
      const urlProgramId = searchParams.get("program");
      if (urlProgramId && programsRes.data.find(p => p.id === urlProgramId)) {
        setSelectedProgram(urlProgramId);
      } else if (programsRes.data.length > 0) {
        setSelectedProgram(programsRes.data[0].id);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Error al cargar datos");
    } finally {
      setLoading(false);
    }
  };

  const fetchWorkoutDays = async (programId) => {
    try {
      const response = await axios.get(
        `${API_URL}/api/admin/workout-days?program_id=${programId}`,
        { headers: getAuthHeaders() }
      );
      setWorkoutDays(response.data);
    } catch (error) {
      console.error("Error fetching workout days:", error);
    }
  };

  const resetForm = () => {
    setFormData({
      program_id: selectedProgram,
      code: "",
      name: "",
      exercises: [],
    });
    setEditingDay(null);
  };

  const openEditDialog = (day) => {
    setEditingDay(day);
    setFormData({
      program_id: day.program_id,
      code: day.code,
      name: day.name || "",
      exercises: day.exercises.map((ex, idx) => ({
        exercise_id: ex.exercise_id,
        sort_order: ex.sort_order || idx + 1,
        sets: ex.sets || 3,
        reps: ex.reps || "12-15",
        rest_seconds: ex.rest_seconds || 60,
        notes: ex.notes || "",
        rir: ex.rir || "",
        superset_group: ex.superset_group || null,
        techniques: ex.techniques || [],
      })),
    });
    setDialogOpen(true);
  };

  const openNewDialog = () => {
    setFormData({
      program_id: selectedProgram,
      code: "",
      name: "",
      exercises: [],
    });
    setDialogOpen(true);
  };

  const addExercise = () => {
    setFormData({
      ...formData,
      exercises: [
        ...formData.exercises,
        {
          exercise_id: "",
          sort_order: formData.exercises.length + 1,
          sets: 3,
          reps: "12-15",
          rest_seconds: 60,
          notes: "",
          rir: "",
          superset_group: null,
          techniques: [],
        },
      ],
    });
  };

  const removeExercise = (index) => {
    const newExercises = formData.exercises.filter((_, i) => i !== index);
    // Recalculate sort_order
    setFormData({
      ...formData,
      exercises: newExercises.map((ex, i) => ({ ...ex, sort_order: i + 1 })),
    });
  };

  const updateExercise = (index, field, value) => {
    const newExercises = [...formData.exercises];
    newExercises[index] = { ...newExercises[index], [field]: value };
    setFormData({ ...formData, exercises: newExercises });
  };

  // Move exercise up/down
  const moveExercise = (index, direction) => {
    const newExercises = [...formData.exercises];
    const newIndex = direction === "up" ? index - 1 : index + 1;
    
    if (newIndex < 0 || newIndex >= newExercises.length) return;
    
    // Swap
    [newExercises[index], newExercises[newIndex]] = [newExercises[newIndex], newExercises[index]];
    
    // Update sort_order
    setFormData({
      ...formData,
      exercises: newExercises.map((ex, i) => ({ ...ex, sort_order: i + 1 })),
    });
  };

  // Toggle superset group
  const toggleSuperset = (index) => {
    const currentGroup = formData.exercises[index].superset_group;
    const newExercises = [...formData.exercises];
    
    if (currentGroup) {
      // Remove from superset
      newExercises[index].superset_group = null;
    } else {
      // Find next available superset group or create new one
      const existingGroups = formData.exercises
        .map(ex => ex.superset_group)
        .filter(g => g !== null);
      
      // Check if previous exercise has a superset group we can join
      if (index > 0 && formData.exercises[index - 1].superset_group) {
        newExercises[index].superset_group = formData.exercises[index - 1].superset_group;
      } else {
        // Create new group
        const maxGroup = Math.max(0, ...existingGroups);
        newExercises[index].superset_group = maxGroup + 1;
        
        // If next exercise exists, add it to the same group
        if (index + 1 < newExercises.length && !newExercises[index + 1].superset_group) {
          newExercises[index + 1].superset_group = maxGroup + 1;
        }
      }
    }
    
    setFormData({ ...formData, exercises: newExercises });
  };

  // Toggle technique for an exercise
  const toggleTechnique = (exerciseIndex, techniqueId) => {
    const newExercises = [...formData.exercises];
    const currentTechniques = newExercises[exerciseIndex].techniques || [];
    
    const existingIndex = currentTechniques.findIndex(t => t.type === techniqueId);
    
    if (existingIndex >= 0) {
      // Remove technique
      newExercises[exerciseIndex].techniques = currentTechniques.filter(t => t.type !== techniqueId);
    } else {
      // Add technique
      newExercises[exerciseIndex].techniques = [...currentTechniques, { type: techniqueId, config: {} }];
    }
    
    setFormData({ ...formData, exercises: newExercises });
  };

  // Update technique config
  const updateTechniqueConfig = (exerciseIndex, techniqueId, configValue) => {
    const newExercises = [...formData.exercises];
    const techniques = newExercises[exerciseIndex].techniques || [];
    
    const techniqueIndex = techniques.findIndex(t => t.type === techniqueId);
    if (techniqueIndex >= 0) {
      techniques[techniqueIndex].config = { value: configValue };
      newExercises[exerciseIndex].techniques = techniques;
      setFormData({ ...formData, exercises: newExercises });
    }
  };

  // Check if exercise has a specific technique
  const hasTechnique = (exerciseIndex, techniqueId) => {
    const techniques = formData.exercises[exerciseIndex]?.techniques || [];
    return techniques.some(t => t.type === techniqueId);
  };

  // Get technique config value
  const getTechniqueConfig = (exerciseIndex, techniqueId) => {
    const techniques = formData.exercises[exerciseIndex]?.techniques || [];
    const technique = techniques.find(t => t.type === techniqueId);
    return technique?.config?.value || "";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.code || !formData.program_id) {
      toast.error("Por favor completa los campos obligatorios");
      return;
    }

    if (formData.exercises.length === 0) {
      toast.error("Agrega al menos un ejercicio");
      return;
    }

    const invalidExercise = formData.exercises.find(ex => !ex.exercise_id);
    if (invalidExercise) {
      toast.error("Selecciona todos los ejercicios");
      return;
    }

    setSaving(true);
    try {
      if (editingDay) {
        await axios.put(
          `${API_URL}/api/admin/workout-days/${editingDay.id}`,
          formData,
          { headers: getAuthHeaders() }
        );
        toast.success("Día actualizado correctamente");
      } else {
        await axios.post(
          `${API_URL}/api/admin/workout-days`,
          formData,
          { headers: getAuthHeaders() }
        );
        toast.success("Día creado correctamente");
      }
      
      fetchWorkoutDays(selectedProgram);
      setDialogOpen(false);
      resetForm();
    } catch (error) {
      console.error("Error saving workout day:", error);
      toast.error("Error al guardar el día");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (dayId) => {
    if (!window.confirm("¿Estás seguro de eliminar este día y todos sus ejercicios?")) return;
    
    setDeleting(dayId);
    try {
      await axios.delete(`${API_URL}/api/admin/workout-days/${dayId}`, {
        headers: getAuthHeaders(),
      });
      toast.success("Día eliminado");
      fetchWorkoutDays(selectedProgram);
    } catch (error) {
      console.error("Error deleting workout day:", error);
      toast.error("Error al eliminar el día");
    } finally {
      setDeleting(null);
    }
  };

  const currentProgram = programs.find(p => p.id === selectedProgram);
  
  // Get superset color
  const getSupersetColor = (group) => {
    if (!group) return null;
    const colors = ["#D4AF37", "#22C55E", "#3B82F6", "#F59E0B", "#EC4899"];
    return colors[(group - 1) % colors.length];
  };

  // Group exercises by superset for display
  const getExerciseGroups = (dayExercises) => {
    const groups = [];
    let currentGroup = [];
    let currentSupersetId = null;

    dayExercises.forEach((ex, idx) => {
      if (ex.superset_group !== currentSupersetId) {
        if (currentGroup.length > 0) {
          groups.push({ superset: currentSupersetId, exercises: currentGroup });
        }
        currentGroup = [ex];
        currentSupersetId = ex.superset_group;
      } else {
        currentGroup.push(ex);
      }
    });

    if (currentGroup.length > 0) {
      groups.push({ superset: currentSupersetId, exercises: currentGroup });
    }

    return groups;
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="admin-workout-days">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-heading font-bold text-white">Días de Entrenamiento</h1>
          <p className="text-muted-foreground mt-1">Configura ejercicios, series, superseries y orden</p>
        </div>
        
        <Button 
          onClick={openNewDialog}
          className="bg-[#D4AF37] hover:bg-[#E5C54D] text-[#0B0B0B] font-bold shadow-lg shadow-[#D4AF37]/20" 
          data-testid="add-workout-day-button"
          disabled={!selectedProgram}
        >
          <Plus className="w-5 h-5 mr-2" />
          Agregar Día
        </Button>
      </div>

      {/* Program Filter */}
      <div className="flex flex-wrap items-center gap-4">
        <Label className="text-white">Programa:</Label>
        <Select value={selectedProgram} onValueChange={setSelectedProgram}>
          <SelectTrigger className="w-64 bg-[#1A1A1A] border-[#2A2A2A] text-white" data-testid="program-filter-select">
            <SelectValue placeholder="Seleccionar programa" />
          </SelectTrigger>
          <SelectContent className="bg-[#121212] border-[#2A2A2A]">
            {programs.map((program) => (
              <SelectItem 
                key={program.id} 
                value={program.id} 
                className="text-white hover:bg-[#1A1A1A]"
              >
                {program.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {currentProgram && (
          <>
            <Badge 
              variant="outline" 
              className={cn(
                currentProgram.track === "men" 
                  ? "border-blue-500/50 text-blue-400" 
                  : "border-pink-500/50 text-pink-400"
              )}
            >
              {currentProgram.track === "men" ? "HOMBRES" : "MUJERES"}
            </Badge>
            <Badge variant="outline" className="border-[#D4AF37]/50 text-[#D4AF37]">
              {currentProgram.days_per_week} días/semana
            </Badge>
          </>
        )}
      </div>

      {/* Workout Days Grid */}
      {loading ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-64 skeleton-loading rounded-2xl" />
          ))}
        </div>
      ) : workoutDays.length === 0 ? (
        <Card className="bg-[#121212] border-[#2A2A2A]">
          <CardContent className="p-8 text-center">
            <Calendar className="w-12 h-12 text-[#555] mx-auto mb-4" />
            <p className="text-muted-foreground mb-4">No hay días configurados para este programa</p>
            <Button 
              onClick={openNewDialog}
              className="bg-[#D4AF37] hover:bg-[#E5C54D] text-[#0B0B0B]"
            >
              <Plus className="w-4 h-4 mr-2" />
              Crear primer día
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
          {workoutDays
            .sort((a, b) => a.code.localeCompare(b.code))
            .map((day) => {
              const exerciseGroups = getExerciseGroups(day.exercises);
              
              return (
                <Card
                  key={day.id}
                  className="bg-[#121212] border-[#2A2A2A] hover:border-[#D4AF37]/30 transition-colors"
                  data-testid={`workout-day-card-${day.code}`}
                >
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      <CardTitle className="flex items-center gap-2">
                        <div className="w-10 h-10 rounded-xl bg-[#D4AF37]/20 border border-[#D4AF37]/30 flex items-center justify-center shrink-0">
                          <span className="text-lg font-heading font-bold text-[#D4AF37]">{day.code}</span>
                        </div>
                        <div className="min-w-0">
                          <span className="text-white font-heading block truncate">
                            {day.name || `Día ${day.code}`}
                          </span>
                          <p className="text-xs text-muted-foreground">
                            {day.name ? `Día ${day.code} • ` : ""}{day.exercises.length} ejercicios
                          </p>
                        </div>
                      </CardTitle>
                      <div className="flex items-center gap-1 shrink-0">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => openEditDialog(day)}
                          className="hover:bg-[#1A1A1A]"
                          data-testid={`edit-day-${day.code}`}
                        >
                          <Pencil className="w-4 h-4 text-muted-foreground" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDelete(day.id)}
                          disabled={deleting === day.id}
                          className="hover:bg-destructive/20"
                          data-testid={`delete-day-${day.code}`}
                        >
                          {deleting === day.id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Trash2 className="w-4 h-4 text-destructive" />
                          )}
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-1">
                      {exerciseGroups.map((group, gIdx) => (
                        <div 
                          key={gIdx}
                          className={cn(
                            "rounded-lg overflow-hidden",
                            group.superset && "border-l-2 pl-2",
                          )}
                          style={group.superset ? { borderColor: getSupersetColor(group.superset) } : {}}
                        >
                          {group.superset && (
                            <div 
                              className="flex items-center gap-1 text-xs mb-1 px-1"
                              style={{ color: getSupersetColor(group.superset) }}
                            >
                              <Link2 className="w-3 h-3" />
                              <span>Superserie</span>
                            </div>
                          )}
                          {group.exercises.map((ex, idx) => (
                            <div
                              key={idx}
                              className="flex items-center gap-2 p-2 rounded-lg bg-[#1A1A1A] mb-1"
                            >
                              <span className="w-5 h-5 rounded-full bg-[#D4AF37]/20 flex items-center justify-center text-[10px] text-[#D4AF37] font-bold shrink-0">
                                {ex.sort_order || idx + 1}
                              </span>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm text-white truncate">
                                  {ex.exercise?.name || "Ejercicio"}
                                </p>
                                <p className="text-xs text-muted-foreground">
                                  {ex.sets}×{ex.reps} • {ex.rest_seconds}s
                                  {ex.rir && ` • RIR ${ex.rir}`}
                                </p>
                              </div>
                            </div>
                          ))}
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
        </div>
      )}

      {/* Dialog */}
      <Dialog open={dialogOpen} onOpenChange={(open) => {
        setDialogOpen(open);
        if (!open) resetForm();
      }}>
        <DialogContent className="bg-[#121212] border-[#2A2A2A] max-w-2xl h-[90vh] sm:h-auto sm:max-h-[85vh] flex flex-col p-0 gap-0 overflow-hidden">
          <DialogHeader className="p-6 pb-4 shrink-0 border-b border-[#2A2A2A]">
            <DialogTitle className="text-white font-heading text-xl">
              {editingDay ? `Editar Día ${editingDay.code}` : "Nuevo Día de Entrenamiento"}
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
            {/* Scrollable content area */}
            <div className="flex-1 overflow-y-auto overscroll-contain px-6 py-4" style={{ WebkitOverflowScrolling: 'touch' }}>
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div className="space-y-2">
                  <Label className="text-white">Programa</Label>
                  <Select
                    value={formData.program_id}
                    onValueChange={(value) => setFormData({ ...formData, program_id: value })}
                  >
                    <SelectTrigger className="bg-[#1A1A1A] border-[#2A2A2A] text-white">
                      <SelectValue placeholder="Seleccionar" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#121212] border-[#2A2A2A]">
                      {programs.map((program) => (
                        <SelectItem key={program.id} value={program.id} className="text-white hover:bg-[#1A1A1A]">
                          {program.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label className="text-white">Código del Día *</Label>
                  <Select
                    value={formData.code}
                    onValueChange={(value) => setFormData({ ...formData, code: value })}
                  >
                    <SelectTrigger className="bg-[#1A1A1A] border-[#2A2A2A] text-white" data-testid="day-code-select">
                      <SelectValue placeholder="Seleccionar" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#121212] border-[#2A2A2A]">
                      {DAY_CODES.map((code) => (
                        <SelectItem key={code} value={code} className="text-white hover:bg-[#1A1A1A]">
                          Día {code}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Day Name Input */}
              <div className="space-y-2 mb-4">
                <Label className="text-white">Nombre del día (opcional)</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="bg-[#1A1A1A] border-[#2A2A2A] text-white"
                  placeholder="Ej: Push (Pecho + Hombro + Tríceps)"
                  data-testid="day-name-input"
                />
                <p className="text-xs text-muted-foreground">
                  Si se deja vacío, se mostrará "Día {formData.code || 'X'}" al usuario
                </p>
              </div>

              {/* Exercises */}
              <div className="flex items-center justify-between mb-3 sticky top-0 bg-[#121212] py-2 -mx-1 px-1 z-10">
                <Label className="text-white">Ejercicios ({formData.exercises.length})</Label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={addExercise}
                  className="border-[#D4AF37]/50 text-[#D4AF37] hover:bg-[#D4AF37]/10"
                  data-testid="add-exercise-to-day"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Agregar
                </Button>
              </div>

              <div className="space-y-3 pb-6">
                {formData.exercises.map((ex, idx) => {
                  const supersetColor = getSupersetColor(ex.superset_group);
                  const exerciseInfo = exercises.find(e => e.id === ex.exercise_id);
                    
                    return (
                      <div
                        key={idx}
                        className={cn(
                          "p-4 rounded-xl bg-[#1A1A1A] border transition-colors",
                          ex.superset_group 
                            ? "border-l-4" 
                            : "border-[#2A2A2A]"
                        )}
                        style={ex.superset_group ? { borderLeftColor: supersetColor } : {}}
                      >
                        {/* Header with order controls */}
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-2">
                            <div className="flex flex-col">
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                onClick={() => moveExercise(idx, "up")}
                                disabled={idx === 0}
                                className="h-6 w-6 hover:bg-[#2A2A2A]"
                              >
                                <ChevronUp className="w-4 h-4 text-muted-foreground" />
                              </Button>
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                onClick={() => moveExercise(idx, "down")}
                                disabled={idx === formData.exercises.length - 1}
                                className="h-6 w-6 hover:bg-[#2A2A2A]"
                              >
                                <ChevronDown className="w-4 h-4 text-muted-foreground" />
                              </Button>
                            </div>
                            <span className="w-8 h-8 rounded-full bg-[#D4AF37]/20 flex items-center justify-center text-sm text-[#D4AF37] font-bold">
                              {idx + 1}
                            </span>
                            {exerciseInfo && (
                              <span className="text-xs text-muted-foreground">
                                {exerciseInfo.muscle_group}
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-1">
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => toggleSuperset(idx)}
                              className={cn(
                                "h-8 px-2",
                                ex.superset_group 
                                  ? "text-[#D4AF37] bg-[#D4AF37]/10" 
                                  : "text-muted-foreground hover:bg-[#2A2A2A]"
                              )}
                              title="Agrupar en superserie"
                            >
                              <Link2 className="w-4 h-4 mr-1" />
                              <span className="text-xs">SS</span>
                            </Button>
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              onClick={() => removeExercise(idx)}
                              className="hover:bg-destructive/20 h-8 w-8"
                            >
                              <X className="w-4 h-4 text-destructive" />
                            </Button>
                          </div>
                        </div>

                        <div className="space-y-3">
                          {/* Exercise Select */}
                          <Select
                            value={ex.exercise_id}
                            onValueChange={(value) => updateExercise(idx, "exercise_id", value)}
                          >
                            <SelectTrigger className="bg-[#121212] border-[#2A2A2A] text-white">
                              <SelectValue placeholder="Seleccionar ejercicio" />
                            </SelectTrigger>
                            <SelectContent className="bg-[#121212] border-[#2A2A2A] max-h-[200px]">
                              {exercises.map((exercise) => (
                                <SelectItem 
                                  key={exercise.id} 
                                  value={exercise.id} 
                                  className="text-white hover:bg-[#1A1A1A]"
                                >
                                  {exercise.name} - {exercise.muscle_group}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>

                          {/* Sets, Reps, Rest, RIR */}
                          <div className="grid grid-cols-4 gap-2">
                            <div>
                              <Label className="text-xs text-muted-foreground">Series</Label>
                              <Input
                                type="number"
                                value={ex.sets}
                                onChange={(e) => updateExercise(idx, "sets", parseInt(e.target.value) || 3)}
                                className="bg-[#121212] border-[#2A2A2A] text-white h-9"
                                min={1}
                                max={10}
                              />
                            </div>
                            <div>
                              <Label className="text-xs text-muted-foreground">Reps</Label>
                              <Input
                                value={ex.reps}
                                onChange={(e) => updateExercise(idx, "reps", e.target.value)}
                                className="bg-[#121212] border-[#2A2A2A] text-white h-9"
                                placeholder="12-15"
                              />
                            </div>
                            <div>
                              <Label className="text-xs text-muted-foreground">Descanso</Label>
                              <Input
                                type="number"
                                value={ex.rest_seconds}
                                onChange={(e) => updateExercise(idx, "rest_seconds", parseInt(e.target.value) || 60)}
                                className="bg-[#121212] border-[#2A2A2A] text-white h-9"
                                min={15}
                                max={300}
                              />
                            </div>
                            <div>
                              <Label className="text-xs text-muted-foreground">RIR</Label>
                              <Input
                                value={ex.rir}
                                onChange={(e) => updateExercise(idx, "rir", e.target.value)}
                                className="bg-[#121212] border-[#2A2A2A] text-white h-9"
                                placeholder="2-3"
                              />
                            </div>
                          </div>

                          {/* Notes */}
                          <div>
                            <Label className="text-xs text-muted-foreground">Notas (opcional)</Label>
                            <Input
                              value={ex.notes}
                              onChange={(e) => updateExercise(idx, "notes", e.target.value)}
                              className="bg-[#121212] border-[#2A2A2A] text-white h-9"
                              placeholder="Ej: Agarre neutro, controlar el peso..."
                            />
                          </div>

                          {/* Intensity Techniques */}
                          <div className="pt-2 border-t border-[#2A2A2A]">
                            <Label className="text-xs text-muted-foreground mb-2 flex items-center gap-1">
                              <Zap className="w-3 h-3" />
                              Técnicas de intensidad
                            </Label>
                            <div className="flex flex-wrap gap-1.5 mt-2">
                              {INTENSITY_TECHNIQUES.map((technique) => {
                                const isActive = hasTechnique(idx, technique.id);
                                const TechIcon = technique.icon;
                                return (
                                  <button
                                    key={technique.id}
                                    type="button"
                                    onClick={() => toggleTechnique(idx, technique.id)}
                                    className={cn(
                                      "px-2 py-1 rounded-lg text-xs font-medium flex items-center gap-1 transition-all border",
                                      isActive
                                        ? "border-transparent"
                                        : "border-[#2A2A2A] bg-[#121212] text-[#666] hover:border-[#444] hover:text-white"
                                    )}
                                    style={isActive ? { backgroundColor: `${technique.color}20`, color: technique.color, borderColor: `${technique.color}50` } : {}}
                                  >
                                    <TechIcon className="w-3 h-3" />
                                    {technique.name}
                                  </button>
                                );
                              })}
                            </div>
                            
                            {/* Config inputs for active techniques */}
                            {(ex.techniques || []).length > 0 && (
                              <div className="mt-3 space-y-2">
                                {(ex.techniques || []).map((activeTech) => {
                                  const techDef = INTENSITY_TECHNIQUES.find(t => t.id === activeTech.type);
                                  if (!techDef?.hasConfig) return null;
                                  const TechIcon = techDef.icon;
                                  return (
                                    <div key={activeTech.type} className="flex items-center gap-2">
                                      <div 
                                        className="flex items-center gap-1 text-xs min-w-[90px]"
                                        style={{ color: techDef.color }}
                                      >
                                        <TechIcon className="w-3 h-3" />
                                        {techDef.configLabel}:
                                      </div>
                                      <Input
                                        type={techDef.configType || "text"}
                                        value={getTechniqueConfig(idx, activeTech.type)}
                                        onChange={(e) => updateTechniqueConfig(idx, activeTech.type, e.target.value)}
                                        className="bg-[#121212] border-[#2A2A2A] text-white h-7 text-xs flex-1"
                                        placeholder={techDef.configPlaceholder}
                                      />
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}

                  {formData.exercises.length === 0 && (
                    <div className="p-8 text-center border border-dashed border-[#2A2A2A] rounded-xl">
                      <Dumbbell className="w-8 h-8 text-[#555] mx-auto mb-2" />
                      <p className="text-muted-foreground text-sm">
                        Agrega ejercicios a este día
                      </p>
                    </div>
                  )}
                </div>
              </div>
            {/* End scrollable content */}

            {/* Fixed footer buttons */}
            <div className="flex gap-3 p-6 pt-4 border-t border-[#2A2A2A] shrink-0 bg-[#121212]">
              <Button
                type="button"
                variant="outline"
                onClick={() => setDialogOpen(false)}
                className="flex-1 border-[#2A2A2A] text-white hover:bg-[#1A1A1A]"
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                disabled={saving}
                className="flex-1 bg-[#D4AF37] hover:bg-[#E5C54D] text-[#0B0B0B] font-bold"
                data-testid="save-workout-day-button"
              >
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Guardando...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    {editingDay ? "Guardar Cambios" : "Crear Día"}
                  </>
                )}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminWorkoutDays;
